// src/utils/csvParser.ts
import Papa from 'papaparse';
import { CSVRowData } from '../types';

const MAX_TEAMS = 500;

/**
 * Validates that a file is a CSV file
 */
export const validateCSVFile = (file: File): { valid: boolean; error?: string } => {
  // Check file extension
  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith('.csv')) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a .csv file',
    };
  }

  // Check file is not empty
  if (file.size === 0) {
    return {
      valid: false,
      error: 'CSV file is empty',
    };
  }

  return { valid: true };
};

/**
 * Validates the row count is within limits
 */
export const validateRowCount = (rowCount: number): { valid: boolean; error?: string } => {
  if (rowCount === 0) {
    return {
      valid: false,
      error: 'CSV file contains no data rows',
    };
  }

  if (rowCount > MAX_TEAMS) {
    return {
      valid: false,
      error: `Your CSV contains ${rowCount} teams. Please split into batches of ${MAX_TEAMS} or fewer.`,
    };
  }

  return { valid: true };
};

/**
 * Parses a CSV file and returns structured data
 * @param file The CSV file to parse
 * @returns Promise with headers, rows, and optional error
 */
export const parseCSV = (
  file: File
): Promise<{ headers: string[]; rows: CSVRowData[]; error?: string }> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true, // Use first row as header
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim(), // Clean up header names
      complete: (results) => {
        // Check if we have headers
        if (!results.meta.fields || results.meta.fields.length === 0) {
          resolve({
            headers: [],
            rows: [],
            error: 'CSV must have a header row',
          });
          return;
        }

        // Identify columns that have at least one non-empty value across all rows
        const columnsWithData = new Set<string>();
        results.data.forEach((row: any) => {
          results.meta.fields?.forEach((header: string) => {
            const value = row[header];
            if (value !== null && value !== undefined && String(value).trim() !== '') {
              columnsWithData.add(header);
            }
          });
        });

        // Keep columns that either:
        // 1. Have a real header name (not empty/auto-generated), OR
        // 2. Have data in at least one row
        // This removes only auto-generated empty columns (like _1, _2 with no data)
        const validHeaders = results.meta.fields.filter((header: string) => {
          const hasRealName = header && header.trim() !== '' && !header.startsWith('_');
          const hasData = columnsWithData.has(header);
          return hasRealName || hasData;
        });

        if (validHeaders.length === 0) {
          resolve({
            headers: [],
            rows: [],
            error: 'CSV must have at least one column with a header or data',
          });
          return;
        }

        // Transform data to include row indices, filtering out rows with all empty values
        const rows: CSVRowData[] = results.data
          .filter((row: any) => {
            // Check if row has at least one non-empty value in valid columns
            return validHeaders.some((header: string) => {
              const value = row[header];
              return value !== null && value !== undefined && String(value).trim() !== '';
            });
          })
          .map((row: any, index: number) => {
            // Only include valid columns in the row data
            const filteredRow: any = { rowIndex: index };
            validHeaders.forEach((header: string) => {
              filteredRow[header] = row[header];
            });
            return filteredRow;
          });

        // Check if we have any actual data after filtering
        if (rows.length === 0) {
          resolve({
            headers: validHeaders,
            rows: [],
            error: 'CSV file contains no data rows',
          });
          return;
        }

        // Validate row count after filtering
        const rowCountValidation = validateRowCount(rows.length);
        if (!rowCountValidation.valid) {
          resolve({
            headers: validHeaders,
            rows: [],
            error: rowCountValidation.error,
          });
          return;
        }

        resolve({
          headers: validHeaders,
          rows,
        });
      },
      error: (error) => {
        resolve({
          headers: [],
          rows: [],
          error: `Failed to parse CSV: ${error.message}`,
        });
      },
    });
  });
};

/**
 * Extracts unique building names from CSV data based on column mapping
 * @param rows CSV rows
 * @param buildingColumnName Name of the column containing building names
 * @returns Array of unique building names
 */
export const extractBuildingNames = (
  rows: CSVRowData[],
  buildingColumnName: string
): string[] => {
  const buildingNames = new Set<string>();

  rows.forEach((row) => {
    const buildingName = row[buildingColumnName];
    if (buildingName && typeof buildingName === 'string' && buildingName.trim()) {
      buildingNames.add(buildingName.trim());
    }
  });

  return Array.from(buildingNames);
};

/**
 * Parses comma-separated domains from a cell value
 * @param domainsString Comma-separated domains string
 * @returns Array of trimmed domain strings, or undefined if empty
 */
export const parseDomains = (domainsString: string | null | undefined): string[] | undefined => {
  if (!domainsString || typeof domainsString !== 'string') {
    return undefined;
  }

  const domains = domainsString
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter((d) => d.length > 0);

  return domains.length > 0 ? domains : undefined;
};

/**
 * Exports import results to CSV format
 * @param results Array of import result items
 * @returns CSV string
 */
export const exportResultsToCSV = (results: any[]): string => {
  // Define the columns we want in the export
  const headers = ['Row #', 'Team Name', 'Status', 'Team ID', 'Error', 'Error Detail'];
  
  // Create CSV rows
  const rows = results.map((result) => {
    return [
      result.row_index + 1, // Add 1 to convert from 0-indexed to 1-indexed for user readability
      result.team_name,
      result.success ? 'Success' : 'Failed',
      result.team_id || '',
      result.error || '',
      result.error_detail || '',
    ];
  });

  // Use Papa.unparse to generate CSV
  const csv = Papa.unparse({
    fields: headers,
    data: rows,
  });

  return csv;
};

/**
 * Triggers a download of a CSV file in the browser
 * @param csvContent CSV content as string
 * @param fileName Name for the downloaded file
 */
export const downloadCSV = (csvContent: string, fileName: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    // Create a link to the file
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
